import { useRef, useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ApiError, fieldErrorsOf } from '@/api/client';
import type { BuildCreateInput } from '@/api/endpoints';
import { useCreateBuilds } from '@/api/hooks';
import type { BuildRequestResult } from '@/api/schemas';
import { humanize } from '@/lib/format';
import { buildPayload, errorOf, hasLocalErrors, validateLocal, type ErrorField } from './runBuildModel';
import type { FieldValues } from './useRunBuildFields';
import type { RunBuildTarget } from './useRunBuildTarget';

/** The state of the user's attempt: whether they tried to submit, the dry-run result and the last server problem. */
export function useRunBuildSession() {
  const [submitted, setSubmitted] = useState(false);
  const [preview, setPreview] = useState<BuildRequestResult | null>(null);
  const [problem, setProblem] = useState<unknown>(null);
  return { submitted, setSubmitted, preview, setPreview, problem, setProblem };
}
export type RunBuildSession = ReturnType<typeof useRunBuildSession>;

/** Local validation of the current values plus the per-field message (server messages win; local ones show after a submit or live). */
export function useRunBuildValidation(t: RunBuildTarget, v: FieldValues, s: RunBuildSession) {
  const local = validateLocal({ hasApp: Boolean(t.app), hasProfile: Boolean(t.group), hasEnvironment: Boolean(t.env), platformCount: t.selectedPlatforms.length, branch: v.branch, commitSha: v.commitSha });
  const serverFields = fieldErrorsOf(s.problem);
  const errorFor = (field: ErrorField, live = false) => errorOf(field, serverFields, local, s.submitted || live);
  return { errorFor, invalid: hasLocalErrors(local) };
}

function payloadFor(t: RunBuildTarget, v: FieldValues, dryRun: boolean): BuildCreateInput | null {
  return buildPayload({
    appSlug: t.app?.slug,
    projectSlug: t.project?.slug,
    profileSlug: t.group?.slug,
    environmentSlug: t.env?.slug,
    platforms: t.selectedPlatforms,
    branch: v.branch,
    commitSha: v.commitSha,
    inputs: v.inputs,
    autoSubmit: v.autoSubmit,
    priority: v.priority,
    message: v.message,
    runnerAllowed: t.runnerAllowed,
    dryRun,
  });
}

interface ActionDeps {
  target: RunBuildTarget;
  values: FieldValues;
  session: RunBuildSession;
  invalid: boolean;
  launchBlock: string | undefined;
  closeRunBuild: () => void;
}

type Create = ReturnType<typeof useCreateBuilds>;

/** Preview: validates, then asks the API for the dry-run plan. */
async function runPreview({ target, values, session, invalid }: ActionDeps, create: Create) {
  session.setSubmitted(true);
  session.setProblem(null);
  const b = payloadFor(target, values, true);
  if (!b || invalid) return;
  try {
    session.setPreview(await create.mutateAsync({ body: b }));
  } catch (e) {
    session.setProblem(e);
  }
}

/** Run: validates (and honours the launch block), requests the builds and rotates the idempotency key after a success. */
async function runSubmit(d: ActionDeps, create: Create, key: { current: string }, navigate: Navigate) {
  d.session.setSubmitted(true);
  d.session.setProblem(null);
  const b = payloadFor(d.target, d.values, false);
  if (!b || d.invalid || d.launchBlock) return;
  try {
    const result = await create.mutateAsync({ body: b, idempotencyKey: key.current });
    key.current = crypto.randomUUID();
    closeAfterRequest(result, d.target.project?.slug, d.closeRunBuild, navigate);
  } catch (err) {
    d.session.setProblem(err);
    if (!(err instanceof ApiError)) toast.error('Could not request the build');
  }
}

/** Preview (dry run) and Run: both validate first. */
export function useRunBuildActions(deps: ActionDeps) {
  const navigate = useNavigate();
  const create = useCreateBuilds();
  const idempotencyKey = useRef(crypto.randomUUID());
  const onPreview = () => runPreview(deps, create);
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    return runSubmit(deps, create, idempotencyKey, navigate);
  };
  return { onPreview, onSubmit, isPending: create.isPending };
}

type Navigate = ReturnType<typeof useNavigate>;

/** Success: toast the new builds, close the sheet and open the first build. */
function closeAfterRequest(result: BuildRequestResult, projectSlug: string | undefined, closeRunBuild: () => void, navigate: Navigate) {
  const first = result.builds[0];
  toast.success(result.builds.length === 1 ? `${first?.ref ?? 'Build'} requested` : `${result.builds.length} builds requested`, {
    description: result.builds.map((x) => `${x.ref} · ${humanize(x.status)}`).join(' · '),
  });
  closeRunBuild();
  if (first && projectSlug) void navigate({ to: '/projects/$slug/builds/$buildId', params: { slug: projectSlug, buildId: first.id } });
}
